import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  OverviewContainer,
  BasicInformation,
  BasicInformationContent,
  BasicInformationHeader,
  LoginInformation,
  LoginInformationContent,
  LoginInformationContentCardWrapper,
  LoginInformationContentCardLeft,
  LoginInformationContentCardRight,
  MultiFactor,
  MultiFactorHeader,
  MultiFactorState,
  MultiFactorStateLeft,
  MultiFactorStateRight,
  InfoItemRow,
  BasicInformationLeft,
  BasicInformationRight, 
  BasicInformationColumn, 
  BasicInformationColumnItem,
  AvatarWrapper
} from "./Overview.styled";
import { Typography, Button, Icon, Card, IconHighlight, Avatar, Tag, Skeleton } from "@ann/web-components";
import { useStore } from "../../../../../../../hooks";
import { getAvatarUrl, generateAvatarColor } from "../../../../../../../utils/avatarUtils";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import EditWorkspaceModal from "../../components/EditWorkspaceModal";

// Format creation date: "Oct 31 · 2025"
const formatCreationDate = (date) => {
  if (!date) return null;
  try {
    return dayjs(date).format("MMM D · YYYY");
  } catch (error) {
    console.error("Error formatting creation date:", error);
    return null;
  }
};

// Format last update: "Nov 2 · 2025 · 10:00 AM"
const formatLastUpdate = (date) => {
  if (!date) return null;
  try {
    return dayjs(date).format("MMM D · YYYY · h:mm A");
  } catch (error) {
    console.error("Error formatting last update:", error);
    return null;
  }
};

const Overview = observer(({ isLoading = false }) => {
  const { workspaceId } = useParams();
  const { workspaceStore } = useStore();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const workspace = workspaceStore.selectedWorkspace;

  // Get workspace data with fallbacks (defensive programming: handle both camelCase and snake_case)
  // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
  const workspaceName = workspace?.workspaceName || workspace?.workspace_name || workspace?.tenantName || workspace?.tenant_name || workspace?.name || 'Unnamed Workspace';
  const contactName = workspace?.contact_name || workspace?.contactName || workspace?.ownerName || workspace?.owner_name || '';
  const contactEmail = workspace?.contact_email || workspace?.contactEmail || workspace?.ownerEmail || workspace?.owner_email || '';
  const address = workspace?.address || '';
  const status = workspace?.status || 'unknown';
  const createdAt = workspace?.created_at || workspace?.createdAt;
  const phoneNumber = workspace?.phone_number || workspace?.phoneNumber || '';
  const userActiveCount = workspace?.user_active_count || workspace?.userActiveCount || 0;
  const userCount = workspace?.user_count || workspace?.userCount || 0;
  const agents = workspace?.agents || 0;
  const workflows = workspace?.workflows || 0;
  const campaigns = workspace?.campaigns || 0;
  
  // Get avatar URL using avatarUtils (defensive programming: handle both camelCase and snake_case)
  const photoUrl = workspace?.photo_url || workspace?.photoUrl || workspace?.profilePhotoUrl;
  const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, workspaceName);
  const avatarColor = generateAvatarColor(workspaceName);
  const showInitialsAvatar = imageFailed || !avatarUrl;
  
  // Get owner avatar (contact)
  const ownerAvatarUrl = getAvatarUrl(null, contactName);
  const ownerAvatarColor = generateAvatarColor(contactName);

  // Get status display - check isActive first (defensive programming: handle both camelCase and snake_case)
  const getStatusDisplay = (workspace) => {
    if (!workspace) return { text: 'Unknown', color: 'neutral' };
    
    // Check isActive field first - if false, show Deactivated
    // Support both camelCase (isActive) and snake_case (is_active)
    const isActive = workspace.isActive !== undefined 
      ? workspace.isActive 
      : workspace.is_active !== undefined 
        ? workspace.is_active 
        : undefined;
    
    if (isActive === false) {
      return { text: 'Deactivated', color: 'error' };
    }
    
    // Otherwise, show status from backend
    const status = workspace.status;
    switch (status?.toLowerCase()) {
      case 'ready':
      case 'active':
        return { text: 'Active', color: 'success' };
      case 'in_progress':
        return { text: 'In Progress', color: 'informative' };
      case 'inactive':
        return { text: 'Inactive', color: 'neutral' };
      default:
        return { text: status || 'Unknown', color: 'neutral' };
    }
  };

  const statusInfo = getStatusDisplay(workspace);

  const handleImageError = () => {
    setImageFailed(true);
  };

  return (
    <OverviewContainer>
      <BasicInformation id="workspace-information">
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Workspace Information
          </Typography>

          <Button 
            type="secondary" 
            label="Edit workspace" 
            size="small" 
            disableEffects="true" 
            iconLeft="edit"
            onClick={() => setIsEditModalOpen(true)}
          />

        </BasicInformationHeader>

        <Card size="medium">
          <BasicInformationContent>
            <BasicInformationLeft>
              <AvatarWrapper>
                {isLoading ? (
                  <Skeleton height="96px" width="96px" />
                ) : !showInitialsAvatar && avatarUrl ? (
                  <Avatar 
                    alt={workspaceName} 
                    size="xl" 
                    src={avatarUrl}
                    onError={handleImageError}
                  />
                ) : (
                  <NameInitialsAvatar
                    name={workspaceName}
                    bgColor={avatarColor}
                    textColor="#ffffff"
                    size="96px"
                    borderRadius="50%"
                    textWeight="bold"
                    textSize="32px"
                    borderWidth="0"
                  />
                )}
              </AvatarWrapper>
            </BasicInformationLeft>
            <BasicInformationRight className="workspace-information-content-right">
              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Name
                  </Typography>
                  {isLoading ? (
                    <Skeleton height="20px" width="150px" />
                  ) : (
                    <Typography category="body" size="medium" weight="regular" color="body">
                      {workspaceName}
                    </Typography>
                  )}
                </BasicInformationColumnItem>

                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Status
                  </Typography>
                  {isLoading ? (
                    <Skeleton height="24px" width="80px" />
                  ) : (
                    <div>
                      <Tag
                        color={statusInfo.color}
                        onClose={() => {}}
                        size="medium"
                        text={statusInfo.text}
                      />
                    </div>
                  )}
                </BasicInformationColumnItem>

              </BasicInformationColumn>

              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Owner
                  </Typography>
                  {isLoading ? (
                    <InfoItemRow gapSpacing="8px">
                      <Skeleton height="32px" width="32px" />
                      <Skeleton height="20px" width="180px" />
                    </InfoItemRow>
                  ) : (
                    <InfoItemRow gapSpacing="8px">
                      {ownerAvatarUrl ? (
                        <Avatar alt={contactName} size="sm" src={ownerAvatarUrl}/>
                      ) : (
                        <NameInitialsAvatar
                          name={contactName}
                          bgColor={ownerAvatarColor}
                          textColor="#ffffff"
                          size="32px"
                          borderRadius="50%"
                          textWeight="bold"
                          textSize="12px"
                          borderWidth="0"
                        />
                      )}
                      <Typography category="body" size="medium" weight="regular" color="body">
                        {contactName || '-'} {contactEmail ? `(${contactEmail})` : ''}
                      </Typography>
                    </InfoItemRow>
                  )}
                </BasicInformationColumnItem>
                {isLoading || address ? (
                  <BasicInformationColumnItem>
                    <Typography category="body" size="medium" weight="regular">
                      Location
                    </Typography>
                    {isLoading ? (
                      <InfoItemRow gapSpacing="8px">
                        <Skeleton height="16px" width="16px" />
                        <Skeleton height="20px" width="200px" />
                      </InfoItemRow>
                    ) : (
                      <InfoItemRow gapSpacing="8px">
                        <Icon name="locationOn" size="small" />
                        <Typography category="body" size="medium" weight="regular" color="body">
                          {address}
                        </Typography>
                      </InfoItemRow>
                    )}
                  </BasicInformationColumnItem>
                ) : null}
              </BasicInformationColumn>

              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Created on
                  </Typography>
                  {isLoading ? (
                    <InfoItemRow gapSpacing="8px">
                      <Skeleton height="16px" width="16px" />
                      <Skeleton height="20px" width="120px" />
                    </InfoItemRow>
                  ) : (
                    <InfoItemRow gapSpacing="8px">
                      <Icon name="calendarToday" size="small" />
                      <Typography category="body" size="medium" weight="regular" color="body">
                        {formatCreationDate(createdAt) || 'N/A'}
                      </Typography>
                    </InfoItemRow>
                  )}
                </BasicInformationColumnItem>
               </BasicInformationColumn>
            </BasicInformationRight>
      
          </BasicInformationContent>
        </Card>
      </BasicInformation>

      <LoginInformation>
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Workspace Statistics
          </Typography>

        </BasicInformationHeader>

        <LoginInformationContent>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Active Users
                </Typography>
                {isLoading ? (
                  <Skeleton height="24px" width="40px" />
                ) : (
                  <span>
                    <Typography category="title" size="medium" weight="bold">
                      {userActiveCount}
                    </Typography>
                  </span>
                )}
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                {!isLoading && <IconHighlight bgColor="success" icon="group" iconColor="success" size="medium"/>}
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Total Agents
                </Typography>
                {isLoading ? (
                  <Skeleton height="24px" width="40px" />
                ) : (
                  <Typography category="title" size="medium" weight="bold">
                    {agents}
                  </Typography>
                )}
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                {!isLoading && <IconHighlight bgColor="success" icon="aiChat" iconColor="success" size="medium"/>}
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Active Workflows
                </Typography>
                {isLoading ? (
                  <Skeleton height="24px" width="80px" />
                ) : (
                  <span>
                    <Typography category="title" size="medium" weight="bold">
                      {workflows}
                    </Typography>
                    <Typography category="body" size="small" weight="regular" color="body">
                      &nbsp;Running
                    </Typography>
                  </span>
                )}
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                {!isLoading && <IconHighlight bgColor="success" icon="bolt" iconColor="success" size="medium"/>}
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>

          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Active Campaigns
                </Typography>
                {isLoading ? (
                  <Skeleton height="24px" width="120px" />
                ) : (
                  <span>
                    <Typography category="title" size="medium" weight="bold">
                      {campaigns}
                    </Typography>
                    <Typography category="body" size="small" weight="regular" color="body">
                      &nbsp;Across Email & SMS
                    </Typography>
                  </span>
                )}
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                {!isLoading && <IconHighlight bgColor="Alert" icon="schedule" iconColor="Alert" size="medium"/>}
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          

        </LoginInformationContent>
      </LoginInformation>

      <EditWorkspaceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        workspace={workspace}
        workspaceId={workspaceId}
      />
    </OverviewContainer>
  );
});

export default Overview;
